import { CreditCardsOverviewGetResponse } from "ApiClient/models";
import { generateCreditCardChartData } from "Helpers/GenerateChartData";
import { classNames } from "@kaladel/react/Utilities";
import { FC, useCallback, useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Header, LoadingError } from "@kaladel/react/Components";
import { Link } from "react-router";
import { DisplayCurrency } from "../DisplayCurrency";
import { DisplayPercentage } from "../DisplayPercentage";
import styles from "./HomeCards.module.scss";
import { useApp } from "AppContext";
import { useApiClient } from "Contexts/ApiClientContext";

interface CreditCardDataItem {
    balance: number;
    nonPromotional: number;
    promotional: number;
    limit: number;
    available: number;
}

export const CreditCardsCard: FC = () => {
    const { api, apiCall } = useApiClient();
    const { activeCurrency } = useApp();

    const [creditCardsOverview, setCreditCardsOverview] = useState<CreditCardsOverviewGetResponse | null>(null);

    const refreshCreditCardsOverview = async () => {
        const response = await apiCall(() => api.creditcards.overview.get());

        if (response.kind === "success") {
            setCreditCardsOverview(response.value);
        }
    };

    const getCreditCardData = useCallback(() => {
        if (!activeCurrency || !creditCardsOverview) return null;

        const currentCurrency = creditCardsOverview.balances.find(b => b.currencyId === activeCurrency);
        const hasAccount = !!currentCurrency;
        const totals: CreditCardDataItem = {
            balance: currentCurrency?.totalBalance ?? 0,
            nonPromotional: currentCurrency?.totalNonPromotional ?? 0,
            promotional: currentCurrency?.totalPromotional ?? 0,
            limit: currentCurrency?.totalLimit ?? 0,
            available: currentCurrency?.totalAvailable ?? 0
        };

        return {
            hasAccount,
            totals,
            chartData: generateCreditCardChartData(activeCurrency, totals)
        };
    }, [activeCurrency, creditCardsOverview]);

    useEffect(() => {
        refreshCreditCardsOverview();
    }, []);

    if (!activeCurrency) return null;

    return (
        <Card>
            <Header title={<Link to="creditcards">Credit Cards <FontAwesomeIcon icon={faChevronRight} /></Link>} level={1} />

            <LoadingError
                value={getCreditCardData()}
                loaded={data => (
                    <>
                        <div className={styles.breakdown}>

                            <div className={styles.line}>
                                <div className={styles.title}>
                                    Total Balance
                                </div>
                                <div className={styles.percentage}>
                                    <DisplayPercentage value={data.totals.balance / data.totals.limit} minimumDP={1} maximumDP={1} />
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.balance} />
                                </div>
                            </div>

                            <div className={classNames([styles.line, styles.indent])}>
                                <div className={styles.title}>
                                    Promotional
                                </div>
                                <div className={styles.percentage}>
                                    <DisplayPercentage value={data.totals.promotional / data.totals.balance} minimumDP={1} maximumDP={1} />
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.promotional} />
                                </div>
                            </div>

                            <div className={classNames([styles.line, styles.indent])}>
                                <div className={styles.title}>
                                    Non-Promotional
                                </div>
                                <div className={styles.percentage}>
                                    <DisplayPercentage value={data.totals.nonPromotional / data.totals.balance} minimumDP={1} maximumDP={1} />
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.nonPromotional} />
                                </div>
                            </div>

                            <div className={styles.line}>
                                <div className={styles.title}>
                                    Available
                                </div>
                                <div className={styles.percentage}>
                                    <DisplayPercentage value={data.totals.available / data.totals.limit} minimumDP={1} maximumDP={1} />
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.available} />
                                </div>
                            </div>

                            <div className={styles.line}>
                                <div className={styles.title}>
                                    Total Credit Limit
                                </div>
                                <div className={styles.percentage}>
                                </div>
                                <div className={styles.value}>
                                    <DisplayCurrency currencyCode={activeCurrency} value={data.totals.limit} />
                                </div>
                            </div>

                            <Pie data={data.chartData} />
                        </div>
                    </>
                )}
            />
        </Card>
    );
};